import { useState, useEffect } from 'react';

// Type pour l'objet window global avec propriétés Electron
declare global {
  interface Window {
    electron?: {
      isElectron: () => boolean;
      toggleFullscreen: () => void;
      saveConfig: (config: any, name?: string) => Promise<{success: boolean; path?: string; error?: string}>;
      loadConfig: (name?: string) => Promise<{success: boolean; config?: any; error?: string}>;
      listConfigs: () => Promise<{success: boolean; configs?: ConfigInfo[]; error?: string}>;
      deleteConfig: (name: string) => Promise<{success: boolean; error?: string}>;
      exportCanvas: (dataUrl: string, filename: string) => Promise<{success: boolean; path?: string; error?: string}>;
      importConfigFile: () => Promise<{success: boolean; config?: any; error?: string}>;
      exportConfigFile: (config: any, filename: string) => Promise<{success: boolean; path?: string; error?: string}>;
    }
  }
}

// Types pour la gestion des configurations
export interface ConfigInfo {
  name: string;
  displayName: string;
  date: string;
  isDefault?: boolean;
}

export interface SaveConfigOptions {
  name?: string;
  displayName?: string;
  makeDefault?: boolean;
}

// Hook pour utiliser les fonctionnalités Electron
export default function useElectron() {
  const [isElectronApp, setIsElectronApp] = useState<boolean>(false);
  const [savedConfigs, setSavedConfigs] = useState<ConfigInfo[]>([]);

  useEffect(() => {
    // Vérifier si l'application est exécutée dans Electron
    const checkIsElectron = async () => {
      if (typeof window !== 'undefined' && window.electron) {
        try {
          const isElectron = window.electron.isElectron();
          setIsElectronApp(isElectron);
        } catch (e) {
          setIsElectronApp(false);
        }
      }
    };

    checkIsElectron();
    loadConfigList();
  }, []);

  // Charger la liste des configurations disponibles
  const loadConfigList = async () => {
    if (isElectronApp && window.electron?.listConfigs) {
      const result = await window.electron.listConfigs();
      if (result.success && result.configs) {
        setSavedConfigs(result.configs);
      }
    } else {
      // Fallback pour navigateur web - utiliser localStorage
      try {
        const configList = localStorage.getItem('visual-grid-config-list');
        if (configList) {
          setSavedConfigs(JSON.parse(configList));
        } else {
          // Vérifier s'il existe une ancienne configuration
          const oldConfig = localStorage.getItem('visual-grid-config');
          if (oldConfig) {
            // Migrer l'ancienne configuration
            const defaultConfig: ConfigInfo = {
              name: 'default',
              displayName: 'Configuration par défaut',
              date: new Date().toISOString(),
              isDefault: true
            };
            localStorage.setItem('visual-grid-config-list', JSON.stringify([defaultConfig]));
            localStorage.setItem('visual-grid-config-default', oldConfig);
            setSavedConfigs([defaultConfig]);
          } else {
            setSavedConfigs([]);
          }
        }
      } catch (error) {
        // console.error('Erreur lors du chargement de la liste des configurations:', error);
        setSavedConfigs([]);
      }
    }
  };

  // Fonction pour activer/désactiver le mode plein écran
  const toggleFullscreen = () => {
    // Utiliser toujours l'API Fullscreen du navigateur pour que seul le contenu passe en plein écran
    // plutôt que toute la fenêtre Electron
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  };

  // Fonction pour sauvegarder une configuration
  const saveConfig = async (config: any, options: SaveConfigOptions = {}) => {
    const { name = 'default', displayName = 'Configuration par défaut', makeDefault = false } = options;
    const configName = name.replace(/[^a-z0-9-_]/gi, '_').toLowerCase();
    
    if (isElectronApp && window.electron) {
      const result = await window.electron.saveConfig(config, configName);
      if (result.success) {
        await loadConfigList();
      }
      return result;
    } else {
      // Fallback pour navigateur web - utiliser localStorage
      try {
        // Sauvegarder la configuration
        localStorage.setItem(`visual-grid-config-${configName}`, JSON.stringify(config));
        
        // Mettre à jour la liste des configurations
        const configInfo: ConfigInfo = {
          name: configName,
          displayName: displayName,
          date: new Date().toISOString(),
          isDefault: makeDefault
        };
        
        let configList = savedConfigs.slice();
        const existingIndex = configList.findIndex(c => c.name === configName);
        
        if (existingIndex >= 0) {
          // Mettre à jour la configuration existante
          configList[existingIndex] = {
            ...configList[existingIndex],
            displayName: displayName, // Mettre à jour le displayName
            date: configInfo.date,
            isDefault: makeDefault
          };
        } else {
          // Ajouter la nouvelle configuration
          configList.push(configInfo);
        }
        
        // Si cette configuration est définie comme par défaut, mettre à jour les autres
        if (makeDefault) {
          configList = configList.map(c => ({
            ...c,
            isDefault: c.name === configName
          }));
        }
        
        localStorage.setItem('visual-grid-config-list', JSON.stringify(configList));
        setSavedConfigs(configList);
        
        return { success: true };
      } catch (error) {
        return { 
          success: false, 
          error: error instanceof Error ? error.message : 'Erreur lors de la sauvegarde'
        };
      }
    }
  };

  // Fonction pour charger une configuration
  const loadConfig = async (name: string = 'default') => {
    if (isElectronApp && window.electron) {
      return window.electron.loadConfig(name);
    } else {
      // Fallback pour navigateur web - utiliser localStorage
      try {
        // Essayer de charger la configuration spécifiée
        let savedConfig = localStorage.getItem(`visual-grid-config-${name}`);
        
        // Si la configuration n'existe pas, essayer de charger la configuration par défaut
        if (!savedConfig && name !== 'default') {
          const defaultConfig = savedConfigs.find(c => c.isDefault);
          if (defaultConfig) {
            savedConfig = localStorage.getItem(`visual-grid-config-${defaultConfig.name}`);
          } else {
            // Essayer l'ancienne méthode
            savedConfig = localStorage.getItem('visual-grid-config');
          }
        }
        
        if (savedConfig) {
          return { success: true, config: JSON.parse(savedConfig) };
        }
        
        return { success: false, error: 'Aucune configuration trouvée' };
      } catch (error) {
        return { 
          success: false, 
          error: error instanceof Error ? error.message : 'Erreur lors du chargement'
        };
      }
    }
  };

  // Fonction pour supprimer une configuration
  const deleteConfig = async (name: string) => {
    if (isElectronApp && window.electron?.deleteConfig) {
      const result = await window.electron.deleteConfig(name);
      if (result.success) {
        await loadConfigList();
      }
      return result;
    } else {
      // Fallback pour navigateur web - utiliser localStorage
      try {
        localStorage.removeItem(`visual-grid-config-${name}`);
        
        // Mettre à jour la liste des configurations
        const configList = savedConfigs.filter(c => c.name !== name);
        localStorage.setItem('visual-grid-config-list', JSON.stringify(configList));
        setSavedConfigs(configList);
        
        return { success: true };
      } catch (error) {
        return { 
          success: false, 
          error: error instanceof Error ? error.message : 'Erreur lors de la suppression'
        };
      }
    }
  };

  // Fonction pour exporter une configuration sous forme de fichier .visualgrid
  const exportConfigFile = async (config: any, filename: string) => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/0fdaebcf-6bf2-451f-980b-2856562df627',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useElectron.ts:exportConfigFile:entry',message:'exportConfigFile called - only exports file, no makeDefault support',data:{filename,isElectronApp,configHasMultiGrid:config?.multiGrid},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'A,C'})}).catch(()=>{});
    // #endregion
    if (isElectronApp && window.electron?.exportConfigFile) {
      return window.electron.exportConfigFile(config, filename);
    } else {
      // Fallback pour navigateur web - créer un lien de téléchargement
      try {
        const configBlob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(configBlob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'config.visualgrid';
        link.click();
        
        // Libérer l'URL
        setTimeout(() => URL.revokeObjectURL(url), 100);
        
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/0fdaebcf-6bf2-451f-980b-2856562df627',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useElectron.ts:exportConfigFile:webSuccess',message:'File download triggered - NOT saved to config list',data:{filename},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'C'})}).catch(()=>{});
        // #endregion
        return { success: true };
      } catch (error) {
        return { 
          success: false, 
          error: error instanceof Error ? error.message : 'Erreur lors de l\'exportation du fichier'
        };
      }
    }
  };

  // Fonction pour importer une configuration depuis un fichier
  // fileType: 'visualgrid' pour page Grid, 'mgrd' pour page MultiGrid
  const importConfigFile = async (fileType: 'visualgrid' | 'mgrd' = 'visualgrid') => {
    const extension = fileType;
    const errorMessage = fileType === 'visualgrid' 
      ? 'Seuls les fichiers .visualgrid sont acceptés' 
      : 'Seuls les fichiers .mgrd sont acceptés';

    if (isElectronApp && window.electron?.importConfigFile) {
      const result = await window.electron.importConfigFile();
      // Si Electron retourne déjà le nom du fichier, on le garde, sinon on l'extrait du chemin si disponible
      if (result && result.success && result.config) {
        return {
          ...result,
          fileName: result.fileName || (result.filePath ? result.filePath.split(/[/\\]/).pop() : undefined)
        };
      }
      return result;
    } else {
      // Fallback pour navigateur web - utiliser l'API File
      return new Promise<{success: boolean; config?: any; fileName?: string; error?: string}>((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = `.${extension}`;
        
        input.onchange = (event) => {
          const file = (event.target as HTMLInputElement).files?.[0];
          if (!file) {
            resolve({ success: false, error: 'Aucun fichier sélectionné' });
            return;
          }
          
          // Vérifier que le fichier a bien l'extension correcte
          if (!file.name.toLowerCase().endsWith(`.${extension}`)) {
            resolve({ success: false, error: errorMessage });
            return;
          }
          
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const content = e.target?.result as string;
              const config = JSON.parse(content);
              resolve({ success: true, config, fileName: file.name });
            } catch (error) {
              resolve({ 
                success: false, 
                error: error instanceof Error ? error.message : 'Erreur lors de la lecture du fichier'
              });
            }
          };
          
          reader.onerror = () => {
            resolve({ success: false, error: 'Erreur lors de la lecture du fichier' });
          };
          
          reader.readAsText(file);
        };
        
        // Déclencher le sélecteur de fichier
        input.click();
      });
    }
  };

  // Fonction pour exporter le canvas
  const exportCanvas = async (dataUrl: string, filename: string) => {
    if (isElectronApp && window.electron) {
      return window.electron.exportCanvas(dataUrl, filename);
    } else {
      // Fallback pour navigateur web - créer un lien de téléchargement
      try {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename || 'visual-grid.png';
        link.click();
        return { success: true };
      } catch (error) {
        return { 
          success: false, 
          error: error instanceof Error ? error.message : 'Erreur lors de l\'exportation'
        };
      }
    }
  };

  return {
    isElectronApp,
    toggleFullscreen,
    saveConfig,
    loadConfig,
    exportCanvas,
    deleteConfig,
    savedConfigs,
    loadConfigList,
    exportConfigFile,
    importConfigFile
  };
}